import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon, PhoneIcon, MailIcon, ChevronDownIcon, ArrowRightIcon } from 'lucide-react';
import { site } from '../data/site';
import { categoryOrder, servicesByCategory } from '../data/services';
import type { ServiceCategory } from '../types/service';
const nav = [{
  to: '/',
  label: 'Home'
}, {
  to: '/services',
  label: 'Services'
}, {
  to: '/service-area',
  label: 'Service Area'
}, {
  to: '/about',
  label: 'About'
}, {
  to: '/specials',
  label: 'Specials & Tips'
}, {
  to: '/contact',
  label: 'Contact'
}];

/** Grouped so the two dropdown columns stay balanced. */
const menuColumns: ServiceCategory[][] = [['Remodeling', 'Home Systems', 'Commercial'], ['Exterior', 'Specialty']];
function ServicesDropdown() {
  return <div className="invisible absolute left-0 top-full z-50 w-[640px] translate-y-1 pt-2 opacity-0 transition duration-150 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100" role="group" aria-label="Service pages" data-id="element-156" data-mp="3bbpm6qbx7kg6">
      <div className="overflow-hidden rounded-sm border border-steel-200 bg-white shadow-[0_18px_40px_-16px_rgba(18,24,31,0.35)]" data-id="element-157" data-mp="2mmezl36xsw2l">
        <div className="hatch h-1.5 w-full" aria-hidden="true" data-id="element-158" data-mp="2hovi7uey036f" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-5 p-5" data-id="element-159" data-mp="3fb2jovfy9fek">
          {menuColumns.map((column, i) => <div key={i} className="space-y-5" data-id="element-160" data-mp="3abps5xe4vk5n" data-mpi={i}>
              {column.map(category => <div key={category} data-id="element-161" data-mp="35l0ws6zc2318">
                  <p className="border-b border-steel-200 pb-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-safety" data-id="element-162" data-mp="35jy7g3hns8nf">
                    {category}
                  </p>
                  <ul className="mt-1.5" data-id="element-163" data-mp="1xhd7s4jarvy">
                    {servicesByCategory(category).map(s => <li key={s.slug} data-id="element-164" data-mp="3tabs38xwz57z">
                        <NavLink to={`/services/${s.slug}`} className={({
                  isActive
                }) => `block rounded-sm px-2 py-1.5 text-sm font-medium leading-snug transition ${isActive ? 'bg-safety-100 text-safety-600' : 'text-ink-700 hover:bg-steel-100 hover:text-safety-600'}`} data-id="element-165" data-mp="3t9adwvllq2m1">
                          {s.name}
                        </NavLink>
                      </li>)}
                  </ul>
                </div>)}
            </div>)}
        </div>
        <Link to="/services" className="flex items-center justify-between gap-2 border-t border-steel-200 bg-ink px-5 py-3 text-xs font-bold uppercase tracking-[0.16em] text-white transition hover:bg-ink-800" data-id="element-166" data-mp="13p1thw0gkv4s">
          All Services
          <ArrowRightIcon className="h-4 w-4 text-safety" aria-hidden="true" data-id="element-167" data-mp="meo6p7hxdjn0" />
        </Link>
      </div>
    </div>;
}
export function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);
  return <header className="sticky top-0 z-50 w-full" data-id="element-168" data-mp="re14gnac4b4d">
      <div className="hidden bg-ink text-steel-200 md:block" data-id="element-169" data-mp="rc6ir6r8oez8">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-2 text-xs" data-id="element-170" data-mp="154mls7k54d05">
          <p className="font-medium uppercase tracking-widest text-steel-300" data-id="element-171" data-mp="106umgsh61olx">
            Licensed &amp; Insured Builder · Lead Safe EPA Certified · BBB Member
          </p>
          <div className="flex items-center gap-5" data-id="element-172" data-mp="1a48z0gzyxt8y">
            <a href={site.emailHref} className="flex items-center gap-1.5 transition hover:text-white" data-id="element-173" data-mp="1a4s9jcnahfcy">
              <MailIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-174" data-mp="3bvlwikzlwyuz" />
              {site.email}
            </a>
            <a href={site.estimatePhone.href} className="flex items-center gap-1.5 transition hover:text-white" data-id="element-175" data-mp="22z6uqed6mbgp">
              <PhoneIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-176" data-mp="27vxbz50q3d3p" />
              Estimates &amp; Plumbing: {site.estimatePhone.display}
            </a>
          </div>
        </div>
      </div>

      <div className="border-b border-steel-200 bg-white" data-id="element-177" data-mp="2gxnbky4rdick">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between gap-2 px-3 sm:h-auto sm:gap-4 sm:px-6 sm:py-3" data-id="element-178" data-mp="1k8ywa9kfg96u">
          <Link to="/" className="flex min-w-0 items-center" aria-label="TOTAL Home Improvement — home" data-id="element-179" data-mp="1p7se2e6qymct">
            <img src={site.logo} alt={site.logoAlt} className="h-9 w-auto max-w-[190px] object-contain sm:h-12 sm:max-w-none lg:h-14" data-id="element-180" data-mp="1t7ko6vbsh0ka" />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary" data-id="element-181" data-mp="11fll7p08cxi5">
            {nav.map(item => item.to === '/services' ? <div key={item.to} className="group relative" data-id="element-182" data-mp="xxo3e6u5ru3x">
                  <NavLink to={item.to} className={({
              isActive
            }) => `flex items-center gap-1 rounded-sm px-3 py-2 text-sm font-semibold uppercase tracking-wide transition ${isActive ? 'text-safety' : 'text-ink-700 hover:bg-steel-100 hover:text-ink'}`} data-id="element-183" data-mp="t1qom4yjmvt9">
                    {item.label}
                    <ChevronDownIcon className="h-3.5 w-3.5 text-steel-400 transition group-hover:rotate-180 group-hover:text-safety group-focus-within:rotate-180 group-focus-within:text-safety" aria-hidden="true" data-id="element-184" data-mp="5abie13nbucq" />
                  </NavLink>
                  <ServicesDropdown data-id="element-185" data-mp="3nhr5u03a8iqq" />
                </div> : <NavLink key={item.to} to={item.to} end={item.to === '/'} className={({
            isActive
          }) => `rounded-sm px-3 py-2 text-sm font-semibold uppercase tracking-wide transition ${isActive ? 'text-safety' : 'text-ink-700 hover:bg-steel-100 hover:text-ink'}`} data-id="element-186" data-mp="3dskgtt2p4sug">
                  {item.label}
                </NavLink>)}
          </nav>

          <div className="flex shrink-0 items-center gap-2" data-id="element-187" data-mp="3mu1vd70ggmh1">
            <a href={site.estimatePhone.href} aria-label={`Call ${site.estimatePhone.display}`} className="flex items-center gap-2 rounded-sm bg-safety px-3 py-2.5 text-sm font-bold text-white transition hover:bg-safety-600 sm:hidden" data-id="element-188" data-mp="3mticno2zdu1y">
              <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-189" data-mp="o5cs9ng5insp" />
              <span className="hidden xs:inline" data-id="element-190" data-mp="28tf98y8wk19x">Call</span>
            </a>
            <a href={site.estimatePhone.href} className="hidden items-center gap-2 rounded-sm border-2 border-ink px-3 py-2 text-sm font-bold text-ink transition hover:bg-ink hover:text-white sm:flex" data-id="element-191" data-mp="23v41qu62y2uv">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-192" data-mp="2dm8b9eczkvib" />
              {site.estimatePhone.display}
            </a>
            <Link to="/contact" className="hidden rounded-sm bg-safety px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-white transition hover:bg-safety-600 md:block" data-id="element-193" data-mp="1pvl08k7rxprj">
              Free Estimate
            </Link>
            <button type="button" onClick={() => setOpen(v => !v)} className="rounded-sm border border-steel-300 p-2 text-ink lg:hidden" aria-expanded={open} aria-label={open ? 'Close menu' : 'Open menu'} data-id="element-194" data-mp="1utcrdc0lh7uo">
              {open ? <XIcon className="h-6 w-6" data-id="element-195" data-mp="13oh1nkkgy4u5" /> : <MenuIcon className="h-6 w-6" data-id="element-196" data-mp="18l7f004w2mbv" />}
            </button>
          </div>
        </div>

        {open && <nav className="max-h-[calc(100vh-150px)] overflow-y-auto border-t border-steel-200 bg-white lg:hidden" aria-label="Mobile" data-id="element-197" data-mp="1djiqefau14w7">
            <ul className="mx-auto max-w-7xl px-4 py-2 sm:px-6" data-id="element-198" data-mp="1me6rrudae2fq">
              {nav.map(item => <li key={item.to} data-id="element-199" data-mp="1rbgqk0mkfqg8">
                  <NavLink to={item.to} end={item.to === '/'} className={({
              isActive
            }) => `block border-b border-steel-100 py-3.5 text-base font-semibold uppercase tracking-wide ${isActive ? 'text-safety' : 'text-ink'}`} data-id="element-200" data-mp="1ranjeyjp2yxk">
                    {item.label}
                  </NavLink>

                  {item.to === '/services' && <div className="border-b border-steel-100 pb-3" data-id="element-201" data-mp="2k913yqgdw4jx">
                      {categoryOrder.map(category => <div key={category} className="mt-2" data-id="element-202" data-mp="2becwctnc6ary">
                          <p className="px-1 text-[11px] font-bold uppercase tracking-[0.18em] text-safety" data-id="element-203" data-mp="2beutrtlqiv1g">
                            {category}
                          </p>
                          <ul className="mt-1 border-l-2 border-steel-200 pl-3" data-id="element-204" data-mp="334hh4tf0cztp">
                            {servicesByCategory(category).map(s => <li key={s.slug} data-id="element-205" data-mp="2u4ub1ubc35ub">
                                <NavLink to={`/services/${s.slug}`} className={({
                      isActive
                    }) => `block break-words py-2.5 text-[15px] font-medium leading-snug ${isActive ? 'text-safety' : 'text-ink-600'}`} data-id="element-206" data-mp="2u3j6vtdz7w2o">
                                  {s.name}
                                </NavLink>
                              </li>)}
                          </ul>
                        </div>)}
                    </div>}
                </li>)}
              <li className="space-y-2 py-4" data-id="element-207" data-mp="1vsm7qzrblami">
                <Link to="/contact" className="block rounded-sm bg-safety px-4 py-3.5 text-center text-base font-bold uppercase tracking-wide text-white" data-id="element-208" data-mp="1vvi5amz99xaf">
                  Request a Free Estimate
                </Link>
                <a href={site.estimatePhone.href} className="flex items-center justify-center gap-2 rounded-sm bg-ink px-4 py-3.5 text-base font-bold text-white" data-id="element-209" data-mp="20ttdkt9wjzqj">
                  <PhoneIcon className="h-5 w-5 text-safety" aria-hidden="true" data-id="element-210" data-mp="6gbov2xrgwtx" />
                  {site.estimatePhone.display}
                  <span className="text-xs font-medium uppercase tracking-wide text-steel-300" data-id="element-211" data-mp="2n0xhm8fz8cvb">
                    Estimates &amp; Plumbing
                  </span>
                </a>
                <a href={site.emailHref} className="flex items-center justify-center gap-2 break-all rounded-sm border border-steel-300 px-4 py-3.5 text-sm font-semibold text-ink" data-id="element-212" data-mp="289wvx1q7ymh8">
                  <MailIcon className="h-4 w-4 text-safety" aria-hidden="true" data-id="element-213" data-mp="23dg0rnklr7un" />
                  {site.email}
                </a>
              </li>
            </ul>
          </nav>}
      </div>
      <div className="hatch h-1.5 w-full" aria-hidden="true" data-id="element-214" data-mp="1ubq9qiumuaxe" />
    </header>;
}